import { BffApiClientMolecule } from "@/app/api-client/store"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { Blockchain } from "@anaconda/core/blockchain/types"
import type { NftId } from "@anaconda/core/nft/types"
import { Trans } from "@lingui/react/macro"
import type { Order } from "@rarible/api-client"
import { acceptItemBidFactoryReatom } from "@rarible_int/anaconda_sdk/solana"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import { molecule } from "bunshi"
import { SolanaSdkContainerMoleculeReatom } from "../../blockchains/solana-sdk-container-molecule.reatom"
import { AppToast, SuccessViewTransaction } from "../../toasts/app-toast"
import { getTransactionErrorMessage } from "../events/transaction-event"
import { invalidateNftOffersQueries } from "../order/invalidate-nft-offers-queries"
import { getShownRoyaltyBps } from "./shown-royalty"

export interface SolanaAcceptItemBidActionParams {
  itemId: NftId
  order: Order
}

export const SolanaAcceptItemBidTransactionMoleculeReatom = molecule((mol) => {
  const { sdkContainerAtom } = mol(SolanaSdkContainerMoleculeReatom)
  const { solanaAddress: solanaAddressAtom } = mol(WalletConnectMoleculeReatom)
  const bffApiClient = mol(BffApiClientMolecule)

  const acceptItemBidActionFactory = () => {
    const acceptItemBidAction = action(
      async (params: SolanaAcceptItemBidActionParams) => {
        const from = solanaAddressAtom()
        const container = sdkContainerAtom()

        if (!from) {
          throw new Error(
            "Solana wallet address is not available to accept an offer",
          )
        }

        // Without the royalty the seller saw there is nothing to cap it at,
        // so a failed read stops the accept.
        const maxRoyaltyBps = await wrap(
          getShownRoyaltyBps(bffApiClient, params.itemId),
        )
        const acceptItemBid = acceptItemBidFactoryReatom(container)
        const result = await wrap(
          acceptItemBid({
            from,
            itemId: params.itemId,
            order: params.order,
            maxRoyaltyBps,
          }),
        )
        return { txId: result.txId }
      },
      "solanaAcceptItemBidAction",
    ).extend(
      withAsync(),
      withCallHook(() => {
        sonnerToast.custom(
          (id) => (
            <AppToast.Pending
              id={id}
              title="Accept offer"
              main={<Trans>Accept offer</Trans>}
              second={<Trans>Pending...</Trans>}
            />
          ),
          { duration: Number.POSITIVE_INFINITY },
        )
      }),
    )

    acceptItemBidAction.onReject.extend(
      withCallHook(({ error }) => {
        const message = getTransactionErrorMessage(error)
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Error
            id={id}
            title="Accept offer"
            main={<Trans>Accepting the offer failed</Trans>}
            second={
              <div className="font-semibold text-destructive">{message}</div>
            }
          />
        ))
      }),
    )

    acceptItemBidAction.onFulfill.extend(
      withCallHook(({ payload, params: [params] }) => {
        const { txId } = payload
        invalidateNftOffersQueries(params.itemId)
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Success
            id={id}
            title="Accept offer"
            main={<Trans>Offer accepted</Trans>}
            second={
              <SuccessViewTransaction
                transactionId={txId}
                blockchain={Blockchain.SOLANA}
              />
            }
          />
        ))
      }),
    )

    return acceptItemBidAction
  }

  return { acceptItemBidActionFactory }
})
